/**
 * 모듈 로드 시점에 굳는 문구 방지 — 언어를 바꿔도 따라오는 「게으른 상수」 (2026-09-10)
 *
 * 문제: `export const X = [{ label: tt("a.…", "한국어") }]` 처럼 **모듈 최상위 리터럴 안에서 tt() 를 부르면** 앱이 처음 켜진
 * 언어(기기 언어 — 영어 크롬이면 en)로 문구가 굳는다. 로그인 뒤 계정 언어(한국어)로 바뀌어도 그 문구만 영어로 남았다
 * (홈 코치마크 사고, src/lib/guideFlow.js 는 getter 로 고쳤다).
 *
 * 해결: 리터럴을 `lazyI18n(() => 리터럴)` 로 감싼다. 돌려주는 값은 배열/객체처럼 쓰이는 Proxy 라 **소비처 코드는 그대로** 둔다 —
 * `X.map(...)`, `X[key]`, `Object.keys(X)`, `{...X}`, `for…of`, `Array.isArray(X)` 전부 동작. 읽을 때마다 현재 언어를 보고
 * 언어가 바뀌었으면 리터럴을 다시 만든다(언어별 1회 캐시).
 *
 * 문자열 하나짜리 상수(`const T = tt(...)`)는 Proxy 로 못 만든다 — 함수(`T()`)로 바꾸고 소비처를 고친다.
 * 리터럴 안의 함수/컴포넌트 값은 그대로 돌려준다(bind 하지 않는다 — React 컴포넌트를 bind 하면 매번 새 타입이 돼 리마운트된다).
 */
import i18n, { getI18nResourceVersion } from "./index";

export function lazyI18n(build) {
  let lang = null;
  let built = null;
  const current = () => {
    // 언어 + 사전 판 — 사전은 따로 받으므로(2026-10-29) 같은 언어라도 사전이 들어오면 다시 만든다.
    const cur = `${i18n.language || "ko"}:${getI18nResourceVersion()}`;
    if (built === null || lang !== cur) {
      built = build();
      lang = cur;
    }
    return built;
  };
  const first = current();
  const target = Array.isArray(first) ? [] : {};
  return new Proxy(target, {
    get(_, prop) {
      return Reflect.get(current(), prop);
    },
    has(_, prop) {
      return prop in current();
    },
    ownKeys() {
      return Reflect.ownKeys(current());
    },
    getOwnPropertyDescriptor(t, prop) {
      const d = Object.getOwnPropertyDescriptor(current(), prop);
      if (!d) return undefined;
      // Proxy 불변식: target 에 non-configurable 로 있는 키(배열 length)는 그대로, 나머지는 configurable 로 보고한다
      const own = Object.getOwnPropertyDescriptor(t, prop);
      if (own && !own.configurable) return d;
      return { ...d, configurable: true };
    },
    getPrototypeOf() {
      return Object.getPrototypeOf(current());
    },
  });
}

/** 테스트·디버그용 — 언어별 캐시를 무시하고 지금 언어로 다시 만든 순수 값 */
export function unwrapI18n(proxy) {
  return Array.isArray(proxy) ? [...proxy] : { ...proxy };
}
